import TrainingApp from './TrainingApp';
import {moduleAtRoute} from '@akyo/science-ui/chemistry/module-navigation';
import InteractiveGraphPreview from "./InteractiveGraph";
import { Component, lazy, StrictMode, Suspense, useEffect, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ListChecks } from "lucide-react";
import AuthApp from "./AuthApp";
import CurriculumSettingsApp from "./CurriculumSettingsApp";
import QcmEntryRouter from "./QcmEntryRouter";
import QuizAdminApp from "./QuizAdminApp";
import SecureQuizAdmin from "./SecureQuizAdmin";
import TeacherActivationApp from "./TeacherActivationApp";
import {StandaloneResource} from "./StandaloneResource";
import "./photo-cache-reconcile";
import "./active-session-delete-guard";
import "./qcm-json-compat";
import "./qcm-extra-client";
import "./qcm-results-client";
import "./student-profile-client";
import "./student-qr-client";
import "./student-password-self-client";
import "./student-qr-print-client";
import "./secure-qcm-launch-client";
import "./secure-qcm-offline-lock";
import "./qcm-activity-source-filter";
import "./interactive-activity-entry-client";
import "./styles.css";
import "./quiz.css";
import "./qcm-dashboard.css";
import "./curriculum.css";
import "./admin-shortcuts.css";
import "./period-profile.css";
import "./period-admin-fix.css";
import "./qcm-results-detail.css";
import "./dashboard-kpi-layout.css";
import "./teacher-activation.css";
import "./mobile-student-responsive.css";
import "./student-qr.css";
import "./student-qr-print.css";
import "./secure-qcm.css";
import "./qcm-activity-source-filter.css";
import "./interactive-activity-entry.css";


const QuestionStudio = lazy(() => import("./QuestionStudio"));
const QcmGeneratorStudio = lazy(() => import("./QcmGeneratorStudio"));
const AnimationsPage = lazy(() => import('./AnimationsPage'));
const ScienceRouter = lazy(() => import('./ScienceRouter'));
const StudentPdfView = lazy(() => import("./StudentPdfView"));
const StudentPdfDownload = lazy(() => import("./StudentPdfDownload"));
const MeasurementEngineDemo = lazy(() => import('./MeasurementEngineDemo'));
const MeasurementContextPrototype = lazy(() => import('./MeasurementContextPrototype'));
const ResourcePresentation = lazy(() => import("./ResourcePresentation"));
const QuestionExercise = lazy(() => import("./QuestionExercise"));
const CopperPreview = lazy(() => import("./CopperPreview"));
const AttenuationPreview = lazy(() => import('./AttenuationPreview'));
const LewisPreview = lazy(() => import('./LewisPreview'));
const PhDilutionPreview = lazy(() => import('./PhDilutionPreview'));
const SerumPreview = lazy(() => import('./SerumPreview'));
const AvancementPreview = lazy(() => import('./AvancementPreview'));
const Tp4SolutionsPreview=lazy(()=>import('./Tp4SolutionsPreview'));
const InteractiveActivityStudio = lazy(() => import("./InteractiveActivityStudio"));
const WrittenMobileUpload=lazy(()=>import("./WrittenHomeworks").then(m=>({default:m.WrittenMobileUpload})));
const BoardPhotoUpload=lazy(()=>import('./BoardPhotoUpload'));
const ClassBoard=lazy(()=>import("./ClassBoard"));
const ActivityLibraryView = lazy(() => import("./ActivityLibraryView"));
const ExternalRunApp = lazy(() => import("./ExternalRunApp"));

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  let reloadingForWorker = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadingForWorker) return;
    reloadingForWorker = true;
    window.location.reload();
  });
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", {updateViaCache:"none"})
      .then((registration) => registration.update());
  });
}

const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /dynamically imported module|chunkloaderror|loading chunk|module script|failed to fetch/i.test(message);
};

class RootErrorBoundary extends Component<{children: ReactNode}, {error: Error | null}> {
  state = {error: null as Error | null};

  static getDerivedStateFromError(error: Error) {
    return {error};
  }

  componentDidCatch(error: Error) {
    console.error("CompetenceO render error", error);
    if (!isChunkLoadError(error)) return;
    const retryKey = "competenceo-chunk-retry-v1";
    if (sessionStorage.getItem(retryKey)) return;
    sessionStorage.setItem(retryKey, "1");
    void (async () => {
      try {
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.filter((key) => key.startsWith("competenceo-")).map((key) => caches.delete(key)));
        }
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          await registration?.update();
        }
      } finally {
        window.location.reload();
      }
    })();
  }

  componentDidMount() {
    sessionStorage.removeItem("competenceo-chunk-retry-v1");
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="portal-state">
        <h1>CompetenceO doit être rechargé</h1>
        <p>Une ressource de l’interface n’a pas pu être chargée correctement.</p>
        <button onClick={() => {
          sessionStorage.removeItem("competenceo-chunk-retry-v1");
          window.location.reload();
        }}>Recharger</button>
        <a href="/activity-library">Retour à la bibliothèque</a>
      </main>
    );
  }
}


function AdminShortcuts() {
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    let active = true;
    const check = () => {
      fetch("/api/auth/me", { credentials: "include" })
        .then((response) => response.ok ? response.json() : null)
        .then((body) => { if (active) setAdmin(body?.user?.role === "admin"); })
        .catch(() => { if (active) setAdmin(false); });
    };
    check();
    const timer = window.setInterval(check, 3000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);
  return admin ? (
    <nav className="admin-shortcuts" aria-label="Outils administrateur">
      <a className="admin-nav-shortcut" href="/qcm-admin" title="QCM bêta">
        <ListChecks size={20} />
        <span>QCM</span>
      </a>
    </nav>
  ) : null;
}

function RootApp() {
  const pathname = window.location.pathname;
  if (pathname === '/qcm-training') return <TrainingApp/>;
  if (pathname === '/qcm-training-admin') return <TrainingApp teacher/>;
  if(pathname==='/tableau-photo')return <Suspense fallback={<p>Ouverture du dépôt…</p>}><BoardPhotoUpload/></Suspense>;
  if(pathname==='/tableau-projection')return <Suspense fallback={<p>Ouverture du tableau…</p>}><ClassBoard projection/></Suspense>;
  if(import.meta.env.DEV && pathname === '/dev/tp4-solutions')return <Suspense fallback={<p>Chargement du TP…</p>}><Tp4SolutionsPreview/></Suspense>;
  if(pathname==="/written-upload")return <Suspense fallback={<p>Chargement…</p>}><WrittenMobileUpload/></Suspense>;
  if(pathname === "/external-run")return <Suspense fallback={<p>Chargement de l’activité…</p>}><ExternalRunApp mode="student"/></Suspense>;
  if(pathname === "/external-review")return <Suspense fallback={<p>Chargement de la correction…</p>}><ExternalRunApp mode="review"/></Suspense>;
  if(pathname==='/animations/chimie'||pathname.startsWith('/animations/chimie/')||moduleAtRoute(pathname))return <Suspense fallback={<p>Ouverture de l’atelier scientifique…</p>}><ScienceRouter/></Suspense>;
  if(pathname === '/animations'||pathname === '/animations/')return <Suspense fallback={<p>Chargement des animations…</p>}><AnimationsPage/></Suspense>;
  if(pathname === "/student-copy/print")return <Suspense fallback={<p>Préparation de la copie…</p>}><StudentPdfView/></Suspense>;
  if(pathname === "/student-copy/pdf")return <Suspense fallback={<p>Préparation du PDF…</p>}><StudentPdfDownload/></Suspense>;
  if(pathname === '/animations/laboratoire'||pathname === '/animations/measurement-and-titration/maquette')return <Suspense fallback={<p>Préparation de la paillasse…</p>}><MeasurementContextPrototype/></Suspense>;
  if(pathname === '/animations/measurement-and-titration/demo')return <Suspense fallback={<p>Préparation du laboratoire…</p>}><MeasurementEngineDemo/></Suspense>;
  if(pathname === "/animations/presentation")return <Suspense fallback={<p>Chargement de l’animation…</p>}><ResourcePresentation/></Suspense>;
  if(pathname === "/question-studio")return <Suspense fallback={<p>Chargement des questions…</p>}><QuestionStudio/></Suspense>;
  if(pathname === "/qcm-generator")return <Suspense fallback={<p>Chargement du générateur QCM…</p>}><QcmGeneratorStudio/></Suspense>;
  if(/^\/questions\/exercise\/[^/]+$/.test(pathname))return <Suspense fallback={<p>Chargement de l’exercice…</p>}><QuestionExercise/></Suspense>;
  if(pathname === "/animations/interactive-graph")return <Suspense fallback={<p>Chargement du graphique…</p>}><InteractiveGraphPreview/></Suspense>;
  if(import.meta.env.DEV && pathname === "/dev/attenuation")return <Suspense fallback={<p>Chargement de l’activité…</p>}><AttenuationPreview/></Suspense>;
  if(import.meta.env.DEV && pathname === "/dev/serum")return <Suspense fallback={<p>Chargement de l’activité…</p>}><SerumPreview/></Suspense>;
  if(import.meta.env.DEV && pathname === "/dev/lewis")return <Suspense fallback={<p>Chargement de l’activité…</p>}><LewisPreview/></Suspense>;
  if(import.meta.env.DEV && pathname === "/dev/ph-dilution")return <Suspense fallback={<p>Chargement du TP…</p>}><PhDilutionPreview/></Suspense>;
  if(import.meta.env.DEV && pathname === "/dev/avancement")return <Suspense fallback={<p>Chargement de l’activité…</p>}><AvancementPreview/></Suspense>;
  if(import.meta.env.DEV && pathname === "/dev/tp-cuivre")return <Suspense fallback={<p>Chargement du TP…</p>}><CopperPreview/></Suspense>;
  if(["/animations/measurement-and-titration","/animations/audio-spectrum-analyzer","/animations/young-double-slit","/animations/sound-geometric-attenuation","/animations/acoustic-material-attenuation","/animations/solution-preparation-dissolution","/animations/solution-preparation-dilution","/animations/spectrophotometer"].includes(pathname)) return <StandaloneResource/>;
  if (pathname === "/activity-library" && import.meta.env.DEV && new URLSearchParams(location.search).get("libraryDemo") === "1") return <Suspense fallback={<p>Chargement de la bibliothèque…</p>}><ActivityLibraryView /></Suspense>;
  if (/^\/activation\/[^/]+\/?$/.test(pathname)) return <TeacherActivationApp />;
  if (pathname === "/programmes" || pathname.startsWith("/programmes/")) return <CurriculumSettingsApp />;
  if (pathname === "/activity-studio" || pathname.startsWith("/activity-studio/")) return <Suspense fallback={<p role="status">Chargement du Studio…</p>}><InteractiveActivityStudio /></Suspense>;
  const secureAdminMatch = pathname.match(/^\/qcm-secure-admin\/([^/]+)\/?$/);
  if (secureAdminMatch) return <SecureQuizAdmin sessionId={decodeURIComponent(secureAdminMatch[1])} />;
  if (pathname === "/qcm-admin" || pathname.startsWith("/qcm-admin/")) return <QuizAdminApp />;
  if (/^\/qcm\/[0-9]{6}\/?$/.test(pathname)) return <QcmEntryRouter />;
  return <><AuthApp /><AdminShortcuts /></>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RootErrorBoundary>
      <RootApp />
    </RootErrorBoundary>
  </StrictMode>,
);
